import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  UserIcon,
} from '@heroicons/react/outline';
import {
  getAppointmentStatusLabel,
  getRecordColor,
} from '../../../screens/EpisodesOfCare/helpers';
import useForm from '../../../utils/useForm';
import MetaDataItem from './MetaDataItem';
import EpisodeOfCareDetails from './EpisodeOfCareDetails';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import {
  apiZedocGetPatientProjects,
  apiZedocGetProjectAppointmentMilestones,
  apiZedocGetPatientByIdentifier,
  apiZedocGetBookedClinicalEvents,
} from '../../../common/api/zedoc';
import { VISIT_STATUS_BOOKED } from '../../../common/constants/collections/omop/Visit';

const AppointmentDetails = ({
  appointment,
  visible,
  onCancel,
  isEditable,
  onSave,
  onDelete,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectsOptions, setProjectsOptions] = useState([]);
  const [milestonesOptions, setMilestonesOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [patientIdentifier, setPatientIdentifier] = useState(null);
  const [bookedEventsOptions, setBookedEventsOptions] = useState([]);
  const [loadingBookedEvents, setLoadingBookedEvents] = useState(false);
  const previousPatientIdRef = useRef(null);
  const previousProjectIdRef = useRef(null);
  const currentAppointmentIdRef = useRef(null);

  const { context = {} } = appointment || {};
  const reviewQueueMetadata = appointment?.reviewQueueMetadata;

  const { form, isValid, handleSubmit, renderField, setValue, setValues } =
    useForm({
      inputs: {
        patientName: {
          testId: 'form-field-patient-name',
          label: t('forms:recipientName.label'),
          type: 'text',
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: true,
          muted: true,
          disabled: true,
          schema: {
            type: String,
            optional: true,
          },
        },
        patientId: {
          testId: 'form-field-patient-id',
          label: t('forms:recipientId.label'),
          type: 'text',
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable || !reviewQueueMetadata,
          muted: true,
          disabled: isSubmitting,
          schema: {
            type: String,
          },
        },
        projectId: {
          testId: 'form-field-project-id',
          label: t('forms:projectId.label'),
          type: reviewQueueMetadata && isEditable ? 'react-select' : 'text',
          options: projectsOptions,
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable,
          muted: true,
          disabled: isSubmitting || loadingProjects,
          schema: {
            type: String,
            optional: true,
          },
        },
        milestoneId: {
          testId: 'form-field-roles',
          label: t('milestones', { count: 1 }),
          type: reviewQueueMetadata && isEditable ? 'react-select' : 'text',
          options: milestonesOptions,
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable,
          muted: true,
          disabled: isSubmitting || loadingMilestones,
          schema: {
            type: String,
            optional: true,
          },
        },
        linkedEventId: {
          testId: 'form-field-linked-event-id',
          label: t('forms:linkedEventId.label', {
            defaultValue: 'Link to existing booked appointment',
          }),
          type: 'react-select',
          options: bookedEventsOptions,
          defaultValue: '',
          showValueOnly: !isEditable || !reviewQueueMetadata,
          muted: true,
          disabled: isSubmitting || loadingBookedEvents,
          schema: {
            type: String,
            optional: true,
          },
        },
      },
      onSubmit: async (values) => {
        if (onSave) {
          setIsSubmitting(true);
          try {
            await onSave(values);
            onCancel();
          } catch (error) {
            console.error('Error saving appointment:', error);
          } finally {
            setIsSubmitting(false);
          }
        } else {
          onCancel();
        }
      },
    });

  useEffect(() => {
    const patientId = form.patientId;
    const currentProjectId = form.projectId;

    if (!patientId || !isEditable || !reviewQueueMetadata) {
      setProjectsOptions([]);
      return;
    }

    const namespace =
      appointment?.patientIdentifier?.namespace ||
      'default/national_health_identifier';
    const patientIdentifier = {
      namespace: namespace,
      value: patientId,
    };

    setLoadingProjects(true);
    dispatch(callMethod(apiZedocGetPatientProjects, { patientIdentifier }))
      .then((projects) => {
        const projectsList = projects || [];
        setProjectsOptions(projectsList);

        if (
          currentProjectId &&
          projectsList.length > 0 &&
          !projectsList.some((p) => p.value === currentProjectId)
        ) {
          setValue('projectId', '');
          setValue('milestoneId', '');
        } else if (projectsList.length === 0 && currentProjectId) {
          // No projects found for this patient - clear selection
          setValue('projectId', '');
          setValue('milestoneId', '');
        }
      })
      .catch((error) => {
        setProjectsOptions([]);
        setValue('projectId', '');
        setValue('milestoneId', '');
      })
      .finally(() => {
        setLoadingProjects(false);
      });
  }, [form.patientId, isEditable, reviewQueueMetadata, appointment, dispatch]);

  useEffect(() => {
    const projectId = form.projectId;
    const currentMilestoneId = form.milestoneId;

    previousProjectIdRef.current = projectId;

    if (!projectId || !isEditable || !reviewQueueMetadata) {
      setMilestonesOptions([]);
      if (currentMilestoneId) {
        setValue('milestoneId', '');
      }
      return;
    }

    setLoadingMilestones(true);
    dispatch(callMethod(apiZedocGetProjectAppointmentMilestones, { projectId }))
      .then((milestones) => {
        const milestonesList = milestones || [];
        setMilestonesOptions(milestonesList);

        if (currentMilestoneId) {
          const milestoneExists = milestonesList.some(
            (m) => m.value === currentMilestoneId,
          );
          if (!milestoneExists) {
            setValue('milestoneId', '');
          }
        }
      })
      .catch((error) => {
        setMilestonesOptions([]);
        setValue('milestoneId', '');
      })
      .finally(() => {
        setLoadingMilestones(false);
      });
  }, [form.projectId, isEditable, reviewQueueMetadata, dispatch]);
  useEffect(() => {
    const projectId = form.projectId;
    const patientId = form.patientId;

    if (!projectId || !patientId || !isEditable || !reviewQueueMetadata) {
      setBookedEventsOptions([]);
      return;
    }

    const identifier = {
      namespace:
        appointment?.patientIdentifier?.namespace ||
        'default/national_health_identifier',
      value: patientId,
    };

    setLoadingBookedEvents(true);
    dispatch(
      callMethod(apiZedocGetBookedClinicalEvents, {
        patientIdentifier: identifier,
        projectId,
        clinicalEventsType: 'appointments',
      }),
    )
      .then((events) => {
        setBookedEventsOptions(events || []);
      })
      .catch((error) => {
        console.error('Error fetching booked events:', error);
        setBookedEventsOptions([]);
      })
      .finally(() => {
        setLoadingBookedEvents(false);
      });
  }, [
    form.projectId,
    form.patientId,
    isEditable,
    reviewQueueMetadata,
    dispatch,
    appointment,
  ]);

  useEffect(() => {
    const appointmentId = appointment?._id || appointment?.eventId;

    if (appointment) {
      const isNewAppointment =
        currentAppointmentIdRef.current !== appointmentId;

      // Always update values when appointment data changes (to handle async data loading)
      const identifier = appointment.patientIdentifier || {
        namespace: 'external/omop_person_id',
        value: appointment.patientId,
      };

      // Always update non-editable fields when appointment data changes (to handle async data loading)
      // But only set patientId when switching to a new appointment (to preserve user edits)
      const updates = {
        patientName: appointment.context?.patientName || '',
        projectId:
          isEditable && reviewQueueMetadata
            ? appointment.context?.projectId || ''
            : appointment.context?.projectLabel ||
              appointment.context?.projectId ||
              '',
        milestoneId:
          isEditable && reviewQueueMetadata
            ? appointment.context?.milestoneId || ''
            : appointment.context?.milestoneLabel ||
              appointment.context?.milestoneId ||
              '',
      };

      // Only set patientId when switching to a new appointment
      if (isNewAppointment) {
        updates.patientId =
          appointment.patientId || appointment.patientIdentifier?.value || '';
      }

      setValues(updates);

      if (isNewAppointment) {
        currentAppointmentIdRef.current = appointmentId;
        previousPatientIdRef.current = appointment.patientId;
        previousProjectIdRef.current = appointment.context?.projectId || '';

        setPatientIdentifier(identifier);

        setProjectsOptions([]);
        setMilestonesOptions([]);

        if (
          appointment.patientId &&
          isEditable &&
          reviewQueueMetadata &&
          !appointment.context?.patientName
        ) {
          setLoadingPatient(true);
          dispatch(
            callMethod(apiZedocGetPatientByIdentifier, {
              patientIdentifier: identifier,
            }),
          )
            .then((patient) => {
              if (patient && patient.patientName) {
                setValue('patientName', patient.patientName);
              }
            })
            .catch((error) => {
              console.error(
                'Error fetching patient name for new appointment:',
                error,
              );
            })
            .finally(() => {
              setLoadingPatient(false);
            });
        }

        if (appointment.patientId && isEditable && reviewQueueMetadata) {
          setLoadingProjects(true);
          dispatch(
            callMethod(apiZedocGetPatientProjects, {
              patientIdentifier: identifier,
            }),
          )
            .then((projects) => {
              setProjectsOptions(projects || []);
            })
            .catch((error) => {
              console.error(
                'Error fetching projects for new appointment:',
                error,
              );
              setProjectsOptions([]);
            })
            .finally(() => {
              setLoadingProjects(false);
            });
        }

        if (
          appointment.context?.projectId &&
          isEditable &&
          reviewQueueMetadata
        ) {
          setLoadingMilestones(true);
          dispatch(
            callMethod(apiZedocGetProjectAppointmentMilestones, {
              projectId: appointment.context.projectId,
            }),
          )
            .then((milestones) => {
              setMilestonesOptions(milestones || []);
            })
            .catch((error) => {
              console.error(
                'Error fetching milestones for new appointment:',
                error,
              );
              setMilestonesOptions([]);
            })
            .finally(() => {
              setLoadingMilestones(false);
            });
        }
      }
    }
  }, [
    appointment,
    isEditable,
    reviewQueueMetadata,
    dispatch,
    setValue,
    setValues,
  ]);

  useEffect(() => {
    const patientId = form.patientId;
    if (!patientId || !isEditable || !reviewQueueMetadata) {
      return;
    }

    if (patientId === previousPatientIdRef.current) {
      return;
    }

    previousPatientIdRef.current = patientId;

    const updatedIdentifier = {
      namespace:
        appointment?.patientIdentifier?.namespace || 'external/omop_person_id',
      value: patientId,
    };

    setPatientIdentifier(updatedIdentifier);

    setLoadingPatient(true);
    dispatch(
      callMethod(apiZedocGetPatientByIdentifier, {
        patientIdentifier: updatedIdentifier,
      }),
    )
      .then((patient) => {
        if (patient && patient.patientName) {
          setValue('patientName', patient.patientName);
        } else {
          setValue('patientName', '');
        }
      })
      .catch((error) => {
        console.error('Error fetching patient by identifier:', error);
        setValue('patientName', '');
      })
      .finally(() => {
        setLoadingPatient(false);
      });
  }, [form.patientId, isEditable, reviewQueueMetadata, appointment, dispatch]);

  const handleSaveAndProcess = async () => {
    if (onSave) {
      setIsSubmitting(true);
      try {
        const values = form;
        await onSave(values, true);
        onCancel();
      } catch (error) {
        console.error('Error saving and processing appointment:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onCancel();
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      setIsSubmitting(true);
      try {
        await onDelete();
        onCancel();
      } catch (error) {
        console.error('Error deleting appointment:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onCancel();
    }
  };

  return (
    <EpisodeOfCareDetails
      title={t('details')}
      visible={visible}
      onCancel={onCancel}
      onSave={handleSubmit}
      onSaveAndProcess={handleSaveAndProcess}
      onDelete={handleDelete}
      isValid={isValid}
      isEditable={isEditable}
    >
      <div className="space-y-4">
        {reviewQueueMetadata && (
          <div
            className={`p-4 rounded-md ${
              reviewQueueMetadata.severity === 'critical'
                ? 'bg-red-100 dark:bg-red-900'
                : reviewQueueMetadata.severity === 'high'
                ? 'bg-yellow-200 dark:bg-yellow-800'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}
          >
            <div className="font-semibold mb-2">
              {reviewQueueMetadata.severity?.toUpperCase()} -{' '}
              {reviewQueueMetadata.type}
            </div>
            <div className="text-sm">
              {reviewQueueMetadata.reasons?.map((reasonObj, index) => (
                <div key={index}>
                  •{' '}
                  {typeof reasonObj === 'string'
                    ? reasonObj
                    : reasonObj.reason || JSON.stringify(reasonObj)}
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-lg font-normal">{t('personalInfo')}</h2>

        <div className="grid grid-cols-2 gap-4">
          {renderField('patientId')}
          {renderField('patientName')}
          {renderField('projectId')}
          {isEditable ? (
            renderField('milestoneId')
          ) : (
            <MetaDataItem
              data-testid="appointment-milestone"
              label={t('milestones', { count: 1 })}
              content={context?.milestoneLabel || context?.milestoneId || '-'}
            />
          )}
        </div>

        <div
          className={`mt-4 ${
            appointment?.status !== VISIT_STATUS_BOOKED &&
            reviewQueueMetadata &&
            isEditable &&
            bookedEventsOptions.length > 0
              ? ''
              : 'hidden'
          }`}
        >
          {renderField('linkedEventId')}
          <p className="text-xs text-neutral-500 mt-1">
            {t('forms:linkedEventId.help', {
              defaultValue:
                'Select an existing booked appointment to update its status instead of creating a new one',
            })}
          </p>
        </div>

        <hr className="border-divider" />

        <h2 className="text-lg font-normal">{t('details')}</h2>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="appointment-type"
            label={t('type')}
            content={context?.type}
          />
          <MetaDataItem
            data-testid="appointment-status"
            label={t('forms:status.label')}
            content={
              <span
                className={`${getRecordColor(
                  appointment?.status,
                )} text-xs px-2 py-1 rounded`}
              >
                {getAppointmentStatusLabel({ status: appointment?.status, t })}
              </span>
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="appointment-date"
            label={t('date')}
            content={
              appointment?.start &&
              toIntlDateTimeFormat(language, appointment.start)
            }
            icon={<CalendarIcon className="w-4 h-4" />}
          />
          <MetaDataItem
            data-testid="appointment-time"
            label={t('time')}
            content={
              appointment?.start &&
              toIntlDateTimeFormat(language, appointment.start, {
                timeStyle: 'short',
              })
            }
            icon={<ClockIcon className="w-4 h-4" />}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="appointment-duration"
            label={t('duration')}
            content={
              appointment?.end && appointment?.start
                ? `${(appointment.end - appointment.start) / 60000} minutes`
                : null
            }
          />
          <MetaDataItem
            data-testid="appointment-location"
            label={t('location')}
            content={context?.location}
            icon={<LocationMarkerIcon className="w-4 h-4" />}
          />
        </div>

        <hr className="border-divider" />

        <MetaDataItem
          data-testid="appointment-clinician"
          label={t('forms:clinician.label')}
          content={context?.clinicianName}
          icon={<UserIcon className="w-4 h-4" />}
        />
        {context?.description && (
          <MetaDataItem
            data-testid="appointment-description"
            label={t('forms:description.label')}
            content={context.description}
          />
        )}
        <MetaDataItem
          data-testid="appointment-notes"
          label={t('notes')}
          content={
            <div className="bg-neutral-400 p-2 rounded mt-1">
              {context?.notes}
            </div>
          }
        />
        <MetaDataItem
          data-testid="appointment-identifier"
          label={t('forms:identifier.label')}
          content={appointment?.eventId}
        />
        <MetaDataItem
          data-testid="appointment-event-type"
          label={t('type')}
          content={t('appointments', { count: 1 })}
        />
      </div>
    </EpisodeOfCareDetails>
  );
};

AppointmentDetails.propTypes = {
  appointment: PropTypes.shape({
    patientId: PropTypes.string,
    eventId: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    status: PropTypes.string,
    context: PropTypes.shape({
      patientName: PropTypes.string,
      projectId: PropTypes.string,
      description: PropTypes.string,
      location: PropTypes.string,
    }),
  }),
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  isEditable: PropTypes.bool,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
};

AppointmentDetails.defaultProps = {
  appointment: null,
  visible: false,
  onCancel: () => {},
  isEditable: false,
  onSave: null,
  onDelete: null,
};

export default AppointmentDetails;
